import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fmtCurrency } from '../lib/display-labels';
import { tenantTodayDate } from '../lib/calendar-date';
import { useTenantTimeZone } from '../hooks/useTenantTimeZone';
/* The CSV cell helpers are IMPORTED. A spreadsheet needs a different contract
   from a screen for the SAME rule: absent is an EMPTY cell there, because a
   '—' sums as zero in Excel. See the head of `lib/csv.ts`. */
import { csvCell, csvMoney } from '../lib/csv';
import { displayValue } from '../features/fieldiq-handoff/contracts';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { unwrap } from '../lib/api-envelope';
import PageHeaderBand, { type PageHeaderStat } from '../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../components/ui/LoadFailureNotice';
import {
  LOAD_FAILURE_QUERY_OPTIONS,
  queryFailureError,
  queryLoadFailure,
} from '../lib/load-failure';
import { Users, Download, DollarSign, Receipt, Hourglass } from 'lucide-react';
import {
  bucketLabel,
  customerLabel,
  type ArAgingCustomerRow,
  type ArAgingReportData,
} from '../types/ar-aging-report';

/**
 * THE RANGE PILLS REACH THE QUERY NOW, AND THAT IS A BEHAVIOUR CHANGE THIS PR
 * OWES AN EXPLANATION FOR.
 *
 * They were dead for as long as this screen existed — `quickRange` changed the
 * highlight and nothing else, because `api.tickets.list()` takes no dates and
 * the client-side derivation below had no date filter. fe#725 reported it and
 * fe#726 restyled around it, both correctly: removing a control is a behaviour
 * change and the blocker was an API that had no range to offer.
 *
 * `GET /reports/ar-aging` takes `dateFrom` / `dateTo`, so the blocker is gone.
 * Leaving four pills dead beside a query that accepts exactly what they say
 * would be the control-that-does-nothing failure with the excuse removed.
 *
 * "ALL TIME" IS THE DEFAULT AND IS A NEW PILL, deliberately. The endpoint's own
 * default is all time up to today in the tenant's zone, and that is the figure
 * api#796 measured per tenant — TNT's $410,937.14 outstanding, Dutch's
 * Coelacanth reading "—". A receivable does not stop being owed because it was
 * raised last quarter, so an aging report that silently opened on this month
 * would under-report every tenant's oldest debt, which is the one thing the
 * 90+ bucket exists to surface.
 *
 * Every date is constructed LOCALLY — `new Date(y, m - 1, d)` — never through
 * `new Date('2026-03-23')`, which parses as UTC midnight and renders as the
 * 22nd west of Greenwich. `work_date` is a calendar day, not an instant.
 */
type QuickRange = 'all' | 'ytd' | 'month' | 'week';

const ranges: { key: QuickRange; label: string }[] = [
  { key: 'all', label: 'All Time' },
  { key: 'ytd', label: 'YTD' },
  { key: 'month', label: 'This Month' },
  { key: 'week', label: 'This Week' },
];

/** `yyyy-MM-dd` for a LOCAL date. Never `toISOString()`, which is UTC's day. */
function localDay(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

/**
 * The params for a pill. `all` sends NOTHING — the server's own default is all
 * time in the TENANT's zone, and reproducing that client-side would put this
 * browser's clock in charge of a tenant's date boundary.
 *
 * The other three are anchored on the TENANT's today for the same reason —
 * never the browser's. With no known zone they are null and the query waits.
 */
function rangeParams(range: QuickRange, timeZone: string | null): { dateFrom?: string; dateTo?: string } | null {
  if (range === 'all') return {};
  const now = tenantTodayDate(timeZone);
  if (!now) return null;
  const to = localDay(now);
  if (range === 'ytd') return { dateFrom: localDay(new Date(now.getFullYear(), 0, 1)), dateTo: to };
  if (range === 'month') return { dateFrom: localDay(new Date(now.getFullYear(), now.getMonth(), 1)), dateTo: to };
  const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - ((now.getDay() + 6) % 7));
  return { dateFrom: localDay(monday), dateTo: to };
}

/** @see fmtCurrency — absence renders '—', not '$0.00'. A real 0 renders as $0.00. */
const money = (v: number | null | undefined) => fmtCurrency(v);

/**
 * The unpriced note, which is what makes an em dash in a money column legible.
 *
 * A dash on its own says "we do not know". A dash beside "6 of 6 unpriced" says
 * WHY, and points at the thing somebody can fix. Three states and three
 * spellings, never two:
 *
 *   every ticket unpriced   the money columns are '—' and this says all of them
 *   some tickets unpriced   the money is a REAL sum that is known to be short
 *   none                    nothing rendered
 */
function UnpricedNote({ row }: { row: ArAgingCustomerRow }) {
  if (row.unpriced_ticket_count === 0) return null;
  const all = row.unpriced_ticket_count === row.ticket_count;
  return (
    <span className="mt-0.5 block text-[10px] text-fieldiq-muted">
      {all
        ? `All ${row.ticket_count} ticket${row.ticket_count === 1 ? '' : 's'} unpriced — there is no figure to show`
        : `${row.unpriced_ticket_count} of ${row.ticket_count} tickets unpriced — the figures below exclude them`}
    </span>
  );
}

export default function CustomerReportScreen() {
  const navigate = useNavigate();
  const [quickRange, setQuickRange] = useState<QuickRange>('all');
  const timeZone = useTenantTimeZone();
  const rangeQuery = rangeParams(quickRange, timeZone);

  /**
   * `GET /reports/ar-aging` (api#796) — AND THE CLIENT-SIDE DERIVATION THIS
   * REPLACES IS DELETED, NOT REPAIRED.
   *
   * This screen used to group `GET /tickets` by customer and sum a money key
   * client-side. fe#725 measured three defects in that block and none of them
   * was fixable here:
   *
   *   1. it read `totalPrice` / `total_price` / `total`, none of which is on
   *      that payload — so `Number(undefined || 0)` was 0 and every customer
   *      read $0 beside a real job count (Vermilion Energy: 43 jobs, $0)
   *   2. "Unknown" was a MISSING JOIN, not a missing customer: `/tickets` does
   *      not join `customers`, so every ticket linked by id collapsed into one
   *      bucket that was not a customer at all
   *   3. there were no aging buckets anywhere in it, so the page could not
   *      answer the question its own name asks
   *
   * The endpoint answers all three at the source: it is `/reports/financial`'s
   * own AR partition and aging, grouped by `customer_id`, so these rows sum to
   * the figures the Financial screen shows for the same range rather than
   * offering a second opinion about them. CLAUDE.md's fifth non-negotiable —
   * nothing here recomputes a figure the API returned.
   *
   * FINANCIAL_READ, so a 403 is an ordinary outcome and gets its own branch.
   * `LOAD_FAILURE_QUERY_OPTIONS` rather than a bare `retry: false`: a 5xx that
   * wants to retry parks in `paused`, where `error` is null and `isLoading`
   * false, and the empty branch would render "no receivables" over a dead
   * server.
   */
  const agingQuery = useQuery({
    queryKey: ['ar-aging', quickRange, rangeQuery],
    queryFn: () =>
      api.reports.arAging(rangeQuery ?? {}).then((r) => unwrap<ArAgingReportData>(r.data)),
    ...LOAD_FAILURE_QUERY_OPTIONS,
    enabled: rangeQuery !== null,
  });
  const { data, isLoading } = agingQuery;
  const failure = queryLoadFailure(agingQuery);

  /**
   * ARRIVED AND PENDING ARE TWO DIFFERENT FACTS (fe#758). `!arrived` is true in
   * flight AND true forever after a failure; the band's `loading` flag draws a
   * pulse, so one boolean for both leaves four stats promising a number that is
   * never coming, beside a panel that has already said the read failed.
   */
  const arrived = !isLoading && !failure && data !== undefined && data !== null;
  const pending = !arrived && !failure;

  const rows: ArAgingCustomerRow[] = data?.rows ?? [];
  const totals = data?.totals;
  const buckets = data?.buckets ?? [];

  function exportCSV() {
    if (!data) return;
    const headers = [
      'Customer', 'Customer Deleted', 'Tickets', 'Unpriced Tickets', 'Outstanding Tickets',
      'Oldest Outstanding Days', 'Revenue', 'Paid', 'Billed Unpaid', 'Unbilled', 'Outstanding',
      ...data.buckets.map((b) => `${bucketLabel(b)} days`),
    ];
    /* An absent figure is an EMPTY cell, never 0.00 — the em-dash rule in the
       one medium where a dash would be read as text. A spreadsheet summing this
       column must not be handed a zero the server declined to state. */
    const lines = data.rows.map((r) => [
      csvCell(customerLabel(r)),
      csvCell(r.customer_deleted ? 'Yes' : 'No'),
      csvCell(r.ticket_count),
      csvCell(r.unpriced_ticket_count),
      csvCell(r.outstanding_ticket_count),
      csvCell(r.oldest_outstanding_days),
      csvMoney(r.revenue), csvMoney(r.paid), csvMoney(r.billed_unpaid),
      csvMoney(r.unbilled), csvMoney(r.outstanding),
      ...data.buckets.map((b) => csvMoney(r.aging[b.key])),
    ].join(','));

    const csv = [headers.map(csvCell).join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ar-aging-${data.range.date_from}-to-${data.range.date_to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  /**
   * FOUR STATS — the only count `PageHeaderBand`'s circle clearance has been
   * measured against across the reference pages. This page shipped two while
   * its money figures were structurally zero and it declined to promote them;
   * they are real figures now, so they go where a reader looks first.
   *
   * SLOT 3 IS "Revenue" (~62px), NOT "Outstanding" (~88px). The circle's 28%
   * amber edge crosses the third column between 1024 and 1048 wide, where a
   * 96px label read 3.88:1 (fe#698), so the long label is kept out of that
   * slot and the order is a measurement rather than a preference.
   *
   * Every money value is `number | null` from the server and passes straight
   * through `fmtCurrency`, which is the one place absence becomes an em dash.
   * `totals.outstanding` is null only when EVERY row is null — a tenant all of
   * whose revenue tickets are unpriced — and an em dash is the right answer
   * there too.
   */
  const bandStats = useMemo((): PageHeaderStat[] => [
    {
      icon: <Users className="size-4" />,
      value: displayValue(arrived ? totals?.customer_count ?? null : null),
      label: 'Customers',
      sub: 'With revenue tickets',
      tone: 'accent',
      loading: pending,
    },
    {
      icon: <Hourglass className="size-4" />,
      value: money(arrived ? totals?.outstanding ?? null : null),
      label: 'Outstanding',
      sub: 'Billed or unbilled',
      tone: 'info',
      loading: pending,
    },
    {
      icon: <DollarSign className="size-4" />,
      value: money(arrived ? totals?.revenue ?? null : null),
      label: 'Revenue',
      sub: 'In this range',
      tone: 'success',
      loading: pending,
    },
    {
      icon: <Receipt className="size-4" />,
      value: displayValue(arrived ? totals?.ticket_count ?? null : null),
      label: 'Tickets',
      sub: 'Counted here',
      loading: pending,
    },
  ], [arrived, pending, totals]);

  return (
    <div data-testid="ar-aging-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      {/* The app shell renders the one global <Header />. Pages never do. */}
      <PageHeaderBand
        eyebrow="Reports"
        // "AR Aging", the name the catalog row uses — and the name is true now.
        // It read "Revenue by Customer", which is ALSO the name of a different,
        // live report in the same catalog (`rev-customer`, GET
        // /reports/by-customer), so two rows opened two screens and one claimed
        // the other's title.
        title="AR Aging"
        onBack={() => navigate('/reports')}
        backLabel="Back to reports"
        actions={
          rows.length > 0 ? (
            <button
              type="button"
              onClick={exportCSV}
              className="fi-action fi-action--standard border border-white/25 hover:border-white/60"
            >
              <Download className="mr-1.5 inline size-4" aria-hidden="true" /> Export CSV
            </button>
          ) : undefined
        }
        stats={bandStats}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <div className="scrollbar-hide flex gap-2 overflow-x-auto" role="group" aria-label="Reporting period">
            {ranges.map((r) => {
              const active = quickRange === r.key;
              return (
                <button
                  key={r.key}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setQuickRange(r.key)}
                  className={`inline-flex flex-shrink-0 items-center whitespace-nowrap rounded-fi-pill px-4 py-1.5 text-xs font-extrabold transition ${
                    active
                      ? 'bg-fi-amber text-fi-amber-ink'
                      : 'border border-fieldiq-border bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text'
                  }`}
                >
                  {r.label}
                </button>
              );
            })}
            {/* The pills carry NO count. They switch which range the page asks
                about — every row is re-read from the server — and a count on a
                control that changes the population would be a count of the
                population you are not looking at. */}
          </div>

          {/* The register — ONE panel, one amber rail, the table inside it. */}
          <section
            aria-labelledby="ar-aging-register-title"
            className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card"
          >
            <header className="border-b border-fieldiq-border px-4 py-4">
              <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">
                Receivables
              </p>
              <h2 id="ar-aging-register-title" className="mt-1 text-base font-extrabold tracking-tight text-fieldiq-text">
                Outstanding by customer, by age
              </h2>
              {arrived && data && (
                <p className="mt-1 text-[11px] text-fieldiq-muted">
                  Aged on {data.aging_key.replace('tickets.', 'ticket ').replace('_', ' ')}, in the company's
                  time zone. {data.range.date_from} to {data.range.date_to}.
                </p>
              )}
            </header>

            {isLoading ? (
              <div className="px-4 py-12 text-center">
                <span
                  role="status"
                  aria-label="Loading receivables"
                  className="mx-auto block h-7 w-40 animate-pulse rounded-fi-control bg-fieldiq-border"
                />
              </div>
            ) : failure ? (
              /* AN ERROR IS NOT AN EMPTY STATE, AND IT GOES WHERE THE EMPTY
                 STATE WAS — not in a banner above one, which is the bug with an
                 apology stapled on. "No revenue data yet." stood here. */
              <LoadFailureNotice
                error={queryFailureError(agingQuery)}
                kind={failure}
                subject="receivables"
                allowed="admins, office staff, supervisors and dispatchers"
              />
            ) : !rows.length ? (
              <div className="px-4 py-12 text-center">
                <Users className="mx-auto mb-3 size-10 text-fieldiq-muted" aria-hidden="true" />
                <p className="text-sm text-fieldiq-text">No revenue tickets in this range.</p>
                <p className="mt-1 text-xs text-fieldiq-muted">
                  A ticket becomes a receivable when the office approves it.
                </p>
              </div>
            ) : (
              <>
                <div className="hidden overflow-x-auto md:block">
                  <table className="w-full min-w-[1180px] text-left">
                    <thead>
                      <tr className="border-b border-fieldiq-border">
                        <th scope="col" className="px-4 py-3 text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Customer</th>
                        <th scope="col" className="px-4 py-3 text-right text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Tickets</th>
                        <th scope="col" className="px-4 py-3 text-right text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Revenue</th>
                        <th scope="col" className="px-4 py-3 text-right text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Paid</th>
                        <th scope="col" className="px-4 py-3 text-right text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Outstanding</th>
                        {/* THE BUCKET HEADINGS COME FROM THE PAYLOAD'S OWN
                            `buckets`, never from four literals here. An edge the
                            API moves would otherwise leave a stale label over a
                            live figure, which is the silent-wrong-value failure
                            in its purest form. */}
                        {buckets.map((b) => (
                          <th key={b.key} scope="col" className="px-4 py-3 text-right text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">
                            {bucketLabel(b)}
                          </th>
                        ))}
                        <th scope="col" className="px-4 py-3 text-right text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Oldest</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        /* NO `tabIndex`, NO `onClick`, NO `role` — the rows have
                           never opened anything and this PR adds no navigation.
                           A row target that leads nowhere is the
                           control-that-does-nothing failure, dressed up.

                           ROW ORDER IS THE SERVER'S: outstanding descending,
                           largest debt first. Nothing here re-sorts. */
                        <tr key={r.customer_id ?? 'no-customer'} className="border-b border-fieldiq-border last:border-b-0 hover:bg-fieldiq-dark/50">
                          <td className="px-4 py-2.5 text-xs font-semibold text-fieldiq-text">
                            <span className="inline-flex items-center gap-2">
                              {customerLabel(r)}
                              {r.customer_deleted && (
                                <span className="inline-flex items-center rounded-fi-pill border border-black/20 bg-fi-signal-slate px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wide text-fi-signal-ink">
                                  Deleted
                                </span>
                              )}
                            </span>
                            <UnpricedNote row={r} />
                          </td>
                          <td className="px-4 py-2.5 text-right text-xs tabular-nums text-fieldiq-muted">{displayValue(r.ticket_count)}</td>
                          <td className="px-4 py-2.5 text-right text-xs tabular-nums text-fieldiq-muted">{money(r.revenue)}</td>
                          <td className="px-4 py-2.5 text-right text-xs tabular-nums text-fieldiq-muted">{money(r.paid)}</td>
                          <td className="px-4 py-2.5 text-right text-xs font-semibold tabular-nums text-fieldiq-text">{money(r.outstanding)}</td>
                          {buckets.map((b) => (
                            <td key={b.key} className="px-4 py-2.5 text-right text-xs tabular-nums text-fieldiq-muted">
                              {money(r.aging[b.key])}
                            </td>
                          ))}
                          <td className="px-4 py-2.5 text-right text-xs tabular-nums text-fieldiq-muted">
                            {r.oldest_outstanding_days === null ? '—' : `${r.oldest_outstanding_days} d`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    {totals && (
                      /* THE TOTALS THE SERVER SUMMED, IN INTEGER CENTS. Nothing
                         on this screen adds a column up — the rows and these
                         figures are one answer, not two. */
                      <tfoot>
                        <tr className="border-t border-fieldiq-border bg-fieldiq-dark/50">
                          <td className="px-4 py-2.5 text-[11px] font-black uppercase tracking-wide text-fieldiq-text">All customers</td>
                          <td className="px-4 py-2.5 text-right text-xs font-semibold tabular-nums text-fieldiq-text">{displayValue(totals.ticket_count)}</td>
                          <td className="px-4 py-2.5 text-right text-xs font-semibold tabular-nums text-fieldiq-text">{money(totals.revenue)}</td>
                          <td className="px-4 py-2.5 text-right text-xs font-semibold tabular-nums text-fieldiq-text">{money(totals.paid)}</td>
                          <td className="px-4 py-2.5 text-right text-sm font-extrabold tabular-nums text-fieldiq-text">{money(totals.outstanding)}</td>
                          {buckets.map((b) => (
                            <td key={b.key} className="px-4 py-2.5 text-right text-xs font-semibold tabular-nums text-fieldiq-text">
                              {money(totals.aging[b.key])}
                            </td>
                          ))}
                          <td />
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>

                {/* Below md the same rows are stacked cards. */}
                <div className="divide-y divide-fieldiq-border md:hidden">
                  {rows.map((r) => (
                    <div key={r.customer_id ?? 'no-customer'} className="space-y-1.5 px-4 py-3">
                      <div className="flex items-start justify-between gap-3">
                        <span className="text-xs font-bold text-fieldiq-text">{customerLabel(r)}</span>
                        <span className="text-xs font-semibold tabular-nums text-fieldiq-text">{money(r.outstanding)}</span>
                      </div>
                      <UnpricedNote row={r} />
                      <div className="flex items-center justify-between text-[10px] text-fieldiq-muted">
                        <span>{r.ticket_count} ticket{r.ticket_count === 1 ? '' : 's'}</span>
                        <span>Revenue: {money(r.revenue)}</span>
                      </div>
                      <dl className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[10px] text-fieldiq-muted">
                        {buckets.map((b) => (
                          <div key={b.key} className="flex items-center justify-between gap-2">
                            <dt>{bucketLabel(b)} days</dt>
                            <dd className="tabular-nums text-fieldiq-text">{money(r.aging[b.key])}</dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  ))}
                </div>

                {/* A footer earns its place by carrying a count, a scope, a
                    permission or a timestamp (CLAUDE.md, fe#711). This is a
                    COUNT and a SCOPE, and the unpriced sentence is the one thing
                    a reader would otherwise get wrong about an em dash above. */}
                <div className="border-t border-fieldiq-border px-4 py-3 text-[11px] text-fieldiq-muted">
                  {rows.length} customer{rows.length === 1 ? '' : 's'} across{' '}
                  {totals ? totals.ticket_count : rows.reduce((s, r) => s + r.ticket_count, 0)} revenue ticket
                  {(totals?.ticket_count ?? 0) === 1 ? '' : 's'}.
                  {totals && totals.unpriced_ticket_count > 0 && (
                    <> {totals.unpriced_ticket_count} carr{totals.unpriced_ticket_count === 1 ? 'ies' : 'y'} no
                      price, so they are in no figure above — an em dash is a ticket with nothing on it to bill,
                      not a balance of zero.</>
                  )}
                </div>
              </>
            )}
          </section>

          {/* THE PDF BUTTON IS GONE AND THE CSV ONE MOVED TO THE BAND.
              Both were restyled-but-unhandled in fe#726 and reported there
              rather than quietly removed, because that was a re-skin. This PR
              owns the page's behaviour, so each one gets an answer instead of
              another report: CSV is wired from the rows already in hand and
              sits with the page's other action on the band; PDF is removed,
              because no endpoint renders one and a client-side PDF of an aging
              register is a feature, not a repair — ten columns wide, it would
              need a print stylesheet of its own. */}
        </div>
      </section>
    </div>
  );
}
